import * as React from "react"
import Mapbox, { MapViewStateChangeEvent } from "../mapbox/Mapbox"
import DrawControl from "../mapbox/DrawControl"
import type { DrawControlType } from "../mapbox/DrawControl"
import { MapLayerMouseEvent, NavigationControl } from "react-map-gl"
import { ZoneType, ZoneEntity, ZonePolygon } from "../lib/types/zone"
import { Bbox, MultiPolygonData, Polygon, ZONES_MAP_PADDING } from "../lib/helpers/map"
import { useMapBBox } from "./providers/MapBBoxProvider"
import ZonesMapLayer from "./ZonesMapLayer"
import { FILL_LAYER_SUFFIX, LINE_LAYER_SUFFIX } from "../mapbox/GeoJSONLayer"

export interface ZonesMapProps {
  type: ZoneType
  filteredZones: ZoneEntity[]
  bbox: Bbox | undefined
  handleDrawCreate: (polygon: { id: string; coords: Polygon }) => void
  handleDrawUpdate: (polygon?: MultiPolygonData) => void
  editableZone: ZoneEntity | null
  isCreating: boolean
  setSelectedZone: (zone: ZoneEntity | undefined) => void
  selectedZone?: ZoneEntity
}

const ZonesMapMapbox: React.FC<ZonesMapProps> = ({
  type,
  filteredZones,
  bbox,
  editableZone,
  handleDrawCreate,
  handleDrawUpdate,
  isCreating,
  setSelectedZone,
  selectedZone,
}) => {
  const drawControlRef = React.useRef<DrawControlType>(null)
  const { setBBoxFromMapbox } = useMapBBox()

  React.useEffect(() => {
    if (isCreating && drawControlRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      drawControlRef.current?.changeMode("draw_polygon")
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreating, drawControlRef.current])

  React.useEffect(() => {
    if (!editableZone || !editableZone?.polygon?.coordinates?.length || !drawControlRef.current) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    drawControlRef.current?.set({
      type: "FeatureCollection",
      features: [
        {
          id: "editableZone",
          type: "Feature",
          geometry: { type: "MultiPolygon", coordinates: editableZone.polygon.coordinates },
          properties: {},
        },
      ],
    })
    handleDrawUpdate(editableZone.polygon.coordinates)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    drawControlRef.current?.changeMode("direct_select", { featureId: "editableZone" })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableZone, handleDrawUpdate, drawControlRef.current])

  const handleUpdate = (e: any) => {
    if (e.features[0].geometry.type === "MultiPolygon") {
      // Simple updates the current editable zone with the new multi polygon data
      handleDrawUpdate(e.features[0].geometry.coordinates)
    } else {
      // If updating a polygon that is a hole, need to overide the zonesToBeCreated value to its new polygon coords
      handleDrawCreate({ id: e.features[0].id, coords: e.features[0].geometry.coordinates[0] })
    }
  }

  const handleCreate = (e: any) => {
    // Adds the first polygon when creating a new zone
    handleDrawCreate({ id: e.features[0].id, coords: e.features[0].geometry.coordinates[0] })
  }

  const handleMapMove = (event: MapViewStateChangeEvent) => {
    if (event.fitboundUpdate) return
    const map = event.target
    const bounds = map.getBounds()
    setBBoxFromMapbox(bounds)
  }

  // get an array of IDs that are also used to create geoJSON layers
  // in order to pass them to the map and make them interactive
  const layersIds = React.useMemo(
    () =>
      filteredZones
        .filter((zone) => zone.polygon)
        .map((zone) => [`${zone.uuid}${FILL_LAYER_SUFFIX}`, `${zone.uuid}${LINE_LAYER_SUFFIX}`])
        .reduce((acc, curr) => [...acc, ...curr], []),
    [filteredZones],
  )

  const onMapClick = React.useCallback(
    (event: MapLayerMouseEvent) => {
      const { features } = event
      const clickedFeature = features && features[0]

      // when user clicks outside a -> zone cancel selection
      if (!clickedFeature) {
        setSelectedZone(undefined)
        return
      }
      const clickedZone = clickedFeature?.properties
      if (!clickedZone) return
      const parsedPolygon = JSON.parse(clickedZone.polygon) as ZonePolygon
      const selectedZone = { ...clickedZone, polygon: parsedPolygon } as ZoneEntity
      setSelectedZone(selectedZone)
    },
    [setSelectedZone],
  )

  return (
    <Mapbox
      fitBounds={bbox}
      fitBoundsOptions={{
        duration: 50,
        linear: true,
        padding: ZONES_MAP_PADDING,
      }}
      style={{
        height: "100%",
        width: "100%",
      }}
      onMoveEnd={handleMapMove}
      interactiveLayerIds={layersIds}
      onClick={onMapClick}
    >
      <>
        <NavigationControl />

        {/* ---------- ZONES ----------- */}
        {filteredZones.map((zone) => {
          if (!zone.polygon) return null
          return (
            <ZonesMapLayer
              key={zone.uuid}
              zone={zone}
              type={type}
              isSelected={selectedZone?.uuid === zone.uuid || false}
            />
          )
        })}

        {(!!editableZone || isCreating) && (
          <DrawControl
            ref={drawControlRef}
            position="bottom-right"
            displayControlsDefault={false}
            controls={{
              polygon: true,
              trash: false,
            }}
            defaultMode="draw_polygon"
            onDrawUpdate={handleUpdate}
            onDrawCreate={handleCreate}
          />
        )}
      </>
    </Mapbox>
  )
}

export default ZonesMapMapbox
